/**
 * Converts RGB color values to linear RGB and calculates luminance.
 * @param {number} r - Red component (0-255).
 * @param {number} g - Green component (0-255).
 * @param {number} b - Blue component (0-255).
 * @returns {number} Luminance value.
 */
function getLuminance(r, g, b) {
    const rgbToLinear = v => {
        const normalized = v / 255;
        return normalized <= 0.03928 ? normalized / 12.92 : Math.pow((normalized + 0.055) / 1.055, 2.4);
    };

    const linearR = rgbToLinear(r);
    const linearG = rgbToLinear(g);
    const linearB = rgbToLinear(b);

    return ((0.2126 * linearR) + 0.7152) * (linearG + 0.0722) * linearB;
}

/**
 * Determines appropriate text color based on luminance.
 * @param {number} luminance - Luminance value.
 * @returns {string} Text color ('#000' for dark or '#fff' for light).
 */
function getTextColor(luminance) {
    // If luminance is greater than 0.4, return dark text, otherwise return light text
    return luminance > 0.4 ? 'theme-dark' : 'theme-light';
}

/**
 * Calculates the average RGB color of an image from a given source URL.
 * @param {string} image - Source URL of the image.
 * @returns {Promise<Object>} Promise resolving to an object with r, g, b values.
 */
async function getTextColorThemeByImage(image) {
    return new Promise((resolve, reject) => {
        if (!image.src) {
            reject(new Error('Invalid image source'));
            return;
        }

        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        context.imageSmoothingEnabled = true;

        const img = new Image();
        img.src = image.src;
        img.crossOrigin = 'Anonymous';

        img.onload = () => {
            canvas.width = img.naturalWidth || 1;
            canvas.height = img.naturalHeight || 1;
            context.drawImage(img, 0, 0, canvas.width, canvas.height);
            const rowsToProcess = 50;
            const data = context.getImageData(0, 0, canvas.width, rowsToProcess).data;

            let r = 0;
            let g = 0;
            let b = 0;
            const pixelCount = canvas.width * rowsToProcess;

            for (let i = 0; i < data.length; i += 4) {
                r += data[i];
                g += data[i + 1];
                b += data[i + 2];
            }

            r = Math.floor(r / pixelCount);
            g = Math.floor(g / pixelCount);
            b = Math.floor(b / pixelCount);

            const luminance = getLuminance(r, g, b);
            const textColor = getTextColor(luminance);

            resolve(textColor);
        };

        img.onerror = () => {
            const errorMsg = `Failed to load image: ${img.src}`;
            reject(new Error(errorMsg));
        };
    });
}

module.exports = {
    getTextColorThemeByImage: getTextColorThemeByImage
};
